import React, { useEffect, useMemo, useState } from 'react';
import FdModal from '../FdModal';
import { useAuth } from '../../context/AuthContext';
import { financeApi, resolveFinanceApiError } from '../../services/financeApi';
import {
  formatCurrency,
  formatDate,
  generateTxnNo,
  normalizeSearch,
  parseNumber,
  todayIso,
} from './utils';
import { matchesDateRange, triggerCsvDownload, triggerExcelLikeDownload } from '../../utils/exportUtils';
import TablePagination, { usePagination } from '../TablePagination';

function createDefaultForm() {
  return {
    date: todayIso(),
    request_type: 'Cash Advance',
    requestor_name: '',
    branch_id: '',
    particulars: [{ description: '', amount: '' }],
  };
}

export default function PaymentRequestsTab({
  paymentRequests,
  branches,
  isLoading,
  isSubmitting,
  onCreateRequest,
  onUpdateRequest,
  canCreateRequest,
  canUpdateRequest,
}) {
  const { isAdmin, userBranchId, userBranchName } = useAuth();
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isReleaseOpen, setIsReleaseOpen] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releaseForm, setReleaseForm] = useState({
    release_reference: '',
    release_date: todayIso(),
  });
  const [form, setForm] = useState(createDefaultForm);
  const [formErrors, setFormErrors] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [viewTarget, setViewTarget] = useState(null);

  useEffect(() => {
    if (!isAdmin && userBranchName) {
      setSelectedBranch(userBranchName);
    }
  }, [isAdmin, userBranchName]);

  useEffect(() => {
    if (!isAdmin && userBranchId != null) {
      setForm((previous) => ({ ...previous, branch_id: String(userBranchId) }));
    }
  }, [isAdmin, userBranchId, isCreateOpen]);

  async function handleAttachmentUpload(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) {
      return;
    }

    setIsUploading(true);
    setUploadError('');
    try {
      const uploaded = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const response = await financeApi.uploadPaymentRequestAttachment(file);
        const attachment = response?.data?.attachment;
        if (attachment?.url) {
          uploaded.push({ name: attachment.name || file.name, url: attachment.url });
        }
      }
      setAttachments((previous) => [...previous, ...uploaded]);
    } catch (err) {
      setUploadError(resolveFinanceApiError(err));
    } finally {
      setIsUploading(false);
    }
  }

  function removeAttachment(index) {
    setAttachments((previous) => previous.filter((_, itemIndex) => itemIndex !== index));
  }

  const branchOptions = useMemo(() => {
    const names = new Set();
    branches.forEach((branch) => {
      if (branch?.name) {
        names.add(branch.name);
      }
    });
    paymentRequests.forEach((row) => {
      if (row?.branch) {
        names.add(row.branch);
      }
    });
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [branches, paymentRequests]);

  const filteredRows = useMemo(() => {
    const searchKey = normalizeSearch(search);

    return paymentRequests
      .filter((row) => selectedBranch === 'all' || String(row.branch || '') === selectedBranch)
      .filter((row) => {
        if (!searchKey) {
          return true;
        }
        const text = [
          row.transaction_no,
          row.requestor_name,
          row.request_type,
          row.branch,
          row.status,
          row.release_reference,
        ]
          .map((value) => normalizeSearch(value))
          .join(' ');
        return text.includes(searchKey);
      })
      .filter((row) => matchesDateRange(row.date, dateFrom, dateTo));
  }, [paymentRequests, selectedBranch, search, dateFrom, dateTo]);

  const pagination = usePagination(filteredRows);

  const EXPORT_HEADERS = ['Txn No.', 'Date', 'Requestor', 'Type', 'Branch', 'Total', 'Status', 'Release Ref'];

  function buildExportRows() {
    return filteredRows.map((row) => [
      row.transaction_no || '',
      formatDate(row.date),
      row.requestor_name || '',
      row.request_type || '',
      row.branch || '',
      Number(row.total_amount || 0).toFixed(2),
      row.status || 'Pending',
      row.release_reference || '',
    ]);
  }

  function exportCsv() {
    triggerCsvDownload(`payment-requests-${todayIso()}.csv`, EXPORT_HEADERS, buildExportRows());
  }

  function exportExcel() {
    triggerExcelLikeDownload(`payment-requests-${todayIso()}.xls`, EXPORT_HEADERS, buildExportRows());
  }

  const total = useMemo(() => form.particulars.reduce(
    (sum, item) => sum + parseNumber(item.amount, 0),
    0
  ), [form.particulars]);

  function addParticular() {
    setForm((previous) => ({
      ...previous,
      particulars: [...previous.particulars, { description: '', amount: '' }],
    }));
  }

  function removeParticular(index) {
    setForm((previous) => {
      if (previous.particulars.length <= 1) {
        return {
          ...previous,
          particulars: [{ description: '', amount: '' }],
        };
      }

      return {
        ...previous,
        particulars: previous.particulars.filter((_, itemIndex) => itemIndex !== index),
      };
    });
  }

  function updateParticular(index, field, value) {
    setForm((previous) => ({
      ...previous,
      particulars: previous.particulars.map((item, itemIndex) => (
        itemIndex === index
          ? { ...item, [field]: value }
          : item
      )),
    }));
  }

  // Particulars and Amount are mandatory: block submission and point out the
  // exact missing field per line item.
  function validateCreateForm() {
    const errors = [];

    const requestorName = String(form.requestor_name || '').trim();
    if (!requestorName) {
      errors.push('Requestor Name is required.');
    }

    // The file attachment is required before a payment request can be submitted.
    if (attachments.length === 0) {
      errors.push('File Attachment is required: upload at least one supporting document.');
    }

    const rows = Array.isArray(form.particulars) ? form.particulars : [];
    const hasAnyEntry = rows.some((item) => String(item.description || '').trim() !== ''
      || String(item.amount ?? '').trim() !== '');
    if (!hasAnyEntry) {
      errors.push('Particulars is required: add at least one line item with a description and an amount.');
      return errors;
    }

    rows.forEach((item, index) => {
      const line = index + 1;
      const description = String(item.description || '').trim();
      const amountText = String(item.amount ?? '').trim();
      const amount = Number(amountText);

      if (!description && !amountText) {
        errors.push(`Line ${line}: Particulars and Amount are required (or remove the empty line).`);
        return;
      }
      if (!description) {
        errors.push(`Line ${line}: Particulars (description) is required.`);
      }
      if (!amountText) {
        errors.push(`Line ${line}: Amount is required.`);
      } else if (!Number.isFinite(amount) || amount <= 0) {
        errors.push(`Line ${line}: Amount must be a number greater than zero.`);
      }
    });

    return errors;
  }

  async function submitCreate() {
    if (!canCreateRequest) {
      return;
    }

    const errors = validateCreateForm();
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors([]);

    const requestorName = String(form.requestor_name || '').trim();
    const branchId = form.branch_id ? Number(form.branch_id) : null;
    const branch = branches.find((item) => Number(item.id) === branchId) || null;
    const branchName = branch?.name || '';

    const particulars = form.particulars
      .map((item) => ({
        description: String(item.description || '').trim(),
        amount: parseNumber(item.amount, 0),
      }))
      .filter((item) => item.description || item.amount > 0);

    const payload = {
      transaction_no: generateTxnNo(form.request_type, branchName),
      date: form.date || null,
      request_type: form.request_type,
      requestor_name: requestorName,
      branch_id: branchId,
      branch: branchName || null,
      branch_name: branchName || null,
      particulars,
      attachments,
      total_amount: particulars.reduce((sum, item) => sum + item.amount, 0),
      status: 'Pending',
    };

    const success = await onCreateRequest(payload);
    if (success) {
      setForm(createDefaultForm());
      setFormErrors([]);
      setAttachments([]);
      setUploadError('');
      setIsCreateOpen(false);
    }
  }

  async function submitReject() {
    if (!isAdmin || !rejectTarget) {
      return;
    }

    const reason = String(rejectReason || '').trim();
    if (!reason) {
      setRejectError('Rejection Reason is required.');
      return;
    }

    const success = await onUpdateRequest(rejectTarget.id, {
      status: 'Rejected',
      rejection_reason: reason,
    });
    if (success) {
      setRejectTarget(null);
      setRejectReason('');
      setRejectError('');
      setReviewTarget(null);
    }
  }

  function openReleaseDialog(row) {
    if (!canUpdateRequest && !isAdmin) {
      return;
    }

    setReleaseTarget(row);
    setReleaseForm({
      release_reference: '',
      release_date: todayIso(),
    });
    setIsReleaseOpen(true);
  }

  async function submitRelease() {
    if (!canUpdateRequest && !isAdmin) {
      return;
    }

    if (!releaseTarget || !releaseForm.release_reference || !releaseForm.release_date) {
      return;
    }

    const success = await onUpdateRequest(releaseTarget.id, {
      status: 'Released',
      release_reference: String(releaseForm.release_reference || '').trim(),
      release_date: releaseForm.release_date,
    });
    if (success) {
      setIsReleaseOpen(false);
      setReleaseTarget(null);
      setReleaseForm({
        release_reference: '',
        release_date: todayIso(),
      });
    }
  }

  async function quickUpdateStatus(row, status) {
    if (!canUpdateRequest && !isAdmin) {
      return;
    }

    const payload = { status };
    if (status === 'For Liquidation') {
      payload.liquidation_status = 'For Liquidation';
    }
    if (status === 'Liquidated') {
      payload.liquidation_status = 'Liquidated';
    }

    await onUpdateRequest(row.id, payload);
  }

  return (
    <div className="finance-tab-content">
      <div className="finance-toolbar">
        <div className="finance-filter-group">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search txn no, requestor, type, status..."
          />
          <select value={selectedBranch} onChange={(event) => setSelectedBranch(event.target.value)} disabled={!isAdmin}>
            {isAdmin ? <option value="all">All Branches</option> : null}
            {branchOptions.map((branchName) => (
              <option key={branchName} value={branchName}>
                {branchName}
              </option>
            ))}
          </select>
          <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} title="Date from" />
          <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} title="Date to" />
        </div>
        <div className="dashboard-actions">
          <button type="button" className="profile-secondary-btn" onClick={exportCsv}>
            Export CSV
          </button>
          <button type="button" className="profile-secondary-btn" onClick={exportExcel}>
            Export Excel
          </button>
          {canCreateRequest ? (
            <button type="button" className="profile-secondary-btn" onClick={() => { setFormErrors([]); setIsCreateOpen(true); }}>
              + New Request
            </button>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <div className="dashboard-loading">Loading payment requests...</div>
      ) : filteredRows.length === 0 ? (
        <div className="dashboard-empty">No payment requests found.</div>
      ) : (
        <div className="dashboard-table-wrap">
          <table className="dashboard-table finance-table">
            <thead>
              <tr>
                <th>Txn No.</th>
                <th>Date</th>
                <th>Requestor</th>
                <th>Type</th>
                <th>Branch</th>
                <th>Total</th>
                <th>Status</th>
                <th>Release Ref</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagination.pagedItems.map((row) => (
                <tr key={row.id}>
                  <td className="finance-mono-cell">{row.transaction_no || '-'}</td>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.requestor_name || '-'}</td>
                  <td>{row.request_type || '-'}</td>
                  <td>{row.branch || '-'}</td>
                  <td className="finance-number-cell">{formatCurrency(row.total_amount)}</td>
                  <td>
                    <span className={`status-badge ${row.status === 'Released' ? 'status-active' : (row.status === 'Rejected' ? 'status-cancelled' : 'status-inactive')}`}>
                      {row.status || 'Pending'}
                    </span>
                    {row.status === 'Rejected' && row.rejection_reason ? (
                      <div className="finance-decline-reason" title={row.rejection_reason}>
                        Reason: {row.rejection_reason}
                      </div>
                    ) : null}
                  </td>
                  <td>{row.release_reference || '-'}</td>
                  <td>
                    <div className="dashboard-actions">
                      <button
                        type="button"
                        className="profile-secondary-btn profile-secondary-btn-xs"
                        onClick={() => setViewTarget(row)}
                      >
                        View
                      </button>

                      {row.status === 'Pending' && isAdmin ? (
                        <button
                          type="button"
                          className="profile-secondary-btn profile-secondary-btn-xs"
                          onClick={() => setReviewTarget(row)}
                          disabled={isSubmitting}
                        >
                          Review
                        </button>
                      ) : null}

                      {row.status === 'Approved' && isAdmin ? (
                        <button
                          type="button"
                          className="profile-secondary-btn profile-secondary-btn-xs"
                          onClick={() => openReleaseDialog(row)}
                          disabled={isSubmitting}
                        >
                          Release
                        </button>
                      ) : null}

                      {row.status === 'Released' && isAdmin ? (
                        <button
                          type="button"
                          className="profile-secondary-btn profile-secondary-btn-xs"
                          onClick={() => quickUpdateStatus(row, 'For Liquidation')}
                          disabled={isSubmitting}
                        >
                          For Liq.
                        </button>
                      ) : null}

                      {row.attachments && row.attachments.length > 0 ? (
                        <span className="finance-attachment-count">{row.attachments.length} file(s)</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading ? <TablePagination {...pagination} /> : null}

      <FdModal
        isOpen={isCreateOpen}
        onClose={() => {
          setIsCreateOpen(false);
          setForm(createDefaultForm());
          setFormErrors([]);
          setAttachments([]);
          setUploadError('');
        }}
        wide
        eyebrow="Finance"
        title="Request for Payment"
        subtitle="Create a new payment request with particulars."
        footer={(
          <>
            <button
              type="button"
              className="fd-btn fd-btn-cancel"
              onClick={() => {
                setIsCreateOpen(false);
                setForm(createDefaultForm());
                setFormErrors([]);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={submitCreate}
              disabled={isSubmitting || isUploading || !canCreateRequest}
            >
              {isSubmitting ? 'Saving...' : 'Submit Request'}
            </button>
          </>
        )}
      >
        <div className="finance-modal-grid">
          {formErrors.length > 0 ? (
            <div className="fd-field fd-field-wide">
              <div className="error-box">
                {formErrors.map((error, index) => (
                  <div key={`form-error-${index}`}>{error}</div>
                ))}
              </div>
            </div>
          ) : null}
          <div className="fd-field">
            <label>Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(event) => setForm((previous) => ({ ...previous, date: event.target.value }))}
            />
          </div>
          <div className="fd-field">
            <label>Type</label>
            <select
              value={form.request_type}
              onChange={(event) => setForm((previous) => ({ ...previous, request_type: event.target.value }))}
            >
              <option value="Cash Advance">Cash Advance</option>
              <option value="Petty Cash">Petty Cash</option>
            </select>
          </div>
          <div className="fd-field fd-field-wide">
            <label>Requestor Name</label>
            <input
              type="text"
              value={form.requestor_name}
              onChange={(event) => setForm((previous) => ({ ...previous, requestor_name: event.target.value }))}
              placeholder="Enter requestor name"
            />
          </div>
          <div className="fd-field fd-field-wide">
            <label>Branch</label>
            <select
              value={form.branch_id}
              onChange={(event) => setForm((previous) => ({ ...previous, branch_id: event.target.value }))}
              disabled={!isAdmin}
            >
              <option value="">Select branch</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>

          <div className="fd-field fd-field-wide">
            <label>Attachments *</label>
            <input
              type="file"
              multiple
              onChange={handleAttachmentUpload}
              disabled={isUploading || isSubmitting}
            />
            {isUploading ? <span className="finance-upload-hint">Uploading...</span> : null}
            {uploadError ? <span className="fd-error-text">{uploadError}</span> : null}
            {attachments.length === 0 && !isUploading ? (
              <span className="finance-inline-note">At least one file attachment is required.</span>
            ) : null}
            {attachments.length > 0 ? (
              <ul className="finance-attachment-list">
                {attachments.map((attachment, index) => (
                  <li key={`attachment-${index}`}>
                    <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                      {attachment.name || 'Attachment'}
                    </a>
                    <button
                      type="button"
                      className="profile-danger-btn profile-danger-btn-xs"
                      onClick={() => removeAttachment(index)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="fd-field fd-field-wide">
            <label>Particulars</label>
            <div className="finance-line-items">
              {form.particulars.map((item, index) => (
                <div key={`particular-${index}`} className="finance-line-item-row">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(event) => updateParticular(index, 'description', event.target.value)}
                    placeholder="Description"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.amount}
                    onChange={(event) => updateParticular(index, 'amount', event.target.value)}
                    placeholder="Amount"
                  />
                  <button
                    type="button"
                    className="profile-danger-btn profile-danger-btn-xs"
                    onClick={() => removeParticular(index)}
                    disabled={form.particulars.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="finance-line-actions">
              <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={addParticular}>
                + Add Item
              </button>
              <span className="finance-line-total">Total: {formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      </FdModal>

      <FdModal
        isOpen={isReleaseOpen}
        onClose={() => setIsReleaseOpen(false)}
        eyebrow="Finance"
        title="Release Payment"
        subtitle="Set release reference and release date."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={() => setIsReleaseOpen(false)} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={submitRelease}
              disabled={isSubmitting || !(canUpdateRequest || isAdmin) || !releaseForm.release_reference}
            >
              {isSubmitting ? 'Saving...' : 'Confirm Release'}
            </button>
          </>
        )}
      >
        <div className="finance-modal-grid">
          <div className="finance-release-preview">
            <p>{releaseTarget?.transaction_no || '-'}</p>
            <span>{releaseTarget?.requestor_name || '-'}</span>
            <strong>{formatCurrency(releaseTarget?.total_amount)}</strong>
          </div>
          <div className="fd-field">
            <label>Release Reference No.</label>
            <input
              type="text"
              value={releaseForm.release_reference}
              onChange={(event) => setReleaseForm((previous) => ({ ...previous, release_reference: event.target.value }))}
              placeholder="e.g. CHK-2026-001"
            />
          </div>
          <div className="fd-field">
            <label>Release Date</label>
            <input
              type="date"
              value={releaseForm.release_date}
              onChange={(event) => setReleaseForm((previous) => ({ ...previous, release_date: event.target.value }))}
            />
          </div>
        </div>
      </FdModal>

      <FdModal
        isOpen={Boolean(reviewTarget)}
        onClose={() => setReviewTarget(null)}
        wide
        eyebrow="Finance"
        title="Review Payment Request"
        subtitle="Review the request details and attachments before approving."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={() => setReviewTarget(null)} disabled={isSubmitting}>
              Close
            </button>
            <button
              type="button"
              className="profile-danger-btn"
              onClick={() => {
                setRejectTarget(reviewTarget);
                setRejectReason('');
                setRejectError('');
              }}
              disabled={isSubmitting}
            >
              Reject
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={async () => {
                const target = reviewTarget;
                setReviewTarget(null);
                await quickUpdateStatus(target, 'Approved');
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Approve'}
            </button>
          </>
        )}
      >
        <div className="finance-modal-grid">
          <div className="finance-release-preview">
            <p>{reviewTarget?.transaction_no || '-'}</p>
            <span>{reviewTarget?.requestor_name || '-'}</span>
            <span>{reviewTarget?.branch || '-'}</span>
            <strong>{formatCurrency(reviewTarget?.total_amount)}</strong>
          </div>
          <div className="fd-field fd-field-wide">
            <label>Attachments</label>
            {reviewTarget?.attachments && reviewTarget.attachments.length > 0 ? (
              <ul className="finance-attachment-list">
                {reviewTarget.attachments.map((attachment) => (
                  <li key={attachment.id || attachment.url}>
                    <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                      {attachment.name || 'Attachment'}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="dashboard-empty">No attachments uploaded for this request.</span>
            )}
          </div>
        </div>
      </FdModal>

      <FdModal
        isOpen={Boolean(rejectTarget)}
        onClose={() => { setRejectTarget(null); setRejectReason(''); setRejectError(''); }}
        eyebrow="Finance"
        title="Reject Payment Request"
        subtitle="Provide a rejection reason. It is saved on the request as part of its history."
        footer={(
          <>
            <button
              type="button"
              className="fd-btn fd-btn-cancel"
              onClick={() => { setRejectTarget(null); setRejectReason(''); setRejectError(''); }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="profile-danger-btn"
              onClick={submitReject}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Confirm Reject'}
            </button>
          </>
        )}
      >
        <div className="finance-modal-grid">
          <div className="finance-release-preview">
            <p>{rejectTarget?.transaction_no || '-'}</p>
            <span>{rejectTarget?.requestor_name || '-'}</span>
            <strong>{formatCurrency(rejectTarget?.total_amount)}</strong>
          </div>
          <div className="fd-field fd-field-wide">
            <label>Rejection Reason *</label>
            <textarea
              className="collections-textarea"
              value={rejectReason}
              onChange={(event) => {
                setRejectReason(event.target.value);
                if (rejectError) {
                  setRejectError('');
                }
              }}
              placeholder="Explain why this payment request is being rejected"
            />
            {rejectError ? <span className="fd-error-text">{rejectError}</span> : null}
          </div>
        </div>
      </FdModal>

      <FdModal
        isOpen={Boolean(viewTarget)}
        onClose={() => setViewTarget(null)}
        wide
        eyebrow="Finance"
        title="Payment Request Details"
        subtitle="View the request details, particulars, and attachments."
        footer={(
          <button type="button" className="fd-btn fd-btn-cancel" onClick={() => setViewTarget(null)}>
            Close
          </button>
        )}
      >
        {viewTarget ? (
          <div className="finance-modal-grid">
            <div className="fd-field">
              <label>Transaction No.</label>
              <input value={viewTarget.transaction_no || '-'} readOnly />
            </div>
            <div className="fd-field">
              <label>Date</label>
              <input value={formatDate(viewTarget.date) || '-'} readOnly />
            </div>
            <div className="fd-field">
              <label>Type</label>
              <input value={viewTarget.request_type || '-'} readOnly />
            </div>
            <div className="fd-field">
              <label>Status</label>
              <input value={viewTarget.status || 'Pending'} readOnly />
            </div>
            <div className="fd-field">
              <label>Requestor</label>
              <input value={viewTarget.requestor_name || '-'} readOnly />
            </div>
            <div className="fd-field">
              <label>Branch</label>
              <input value={viewTarget.branch || viewTarget.branch_name || '-'} readOnly />
            </div>
            <div className="fd-field">
              <label>Total Amount</label>
              <input value={formatCurrency(viewTarget.total_amount)} readOnly />
            </div>
            {viewTarget.release_reference ? (
              <div className="fd-field">
                <label>Release Reference</label>
                <input value={viewTarget.release_reference} readOnly />
              </div>
            ) : null}
            {viewTarget.status === 'Rejected' && viewTarget.rejection_reason ? (
              <div className="fd-field fd-field-wide">
                <label>Rejection Reason</label>
                <textarea className="collections-textarea" value={viewTarget.rejection_reason} readOnly />
              </div>
            ) : null}

            <div className="fd-field fd-field-wide">
              <label>Particulars</label>
              {Array.isArray(viewTarget.particulars) && viewTarget.particulars.length > 0 ? (
                <ul className="finance-attachment-list">
                  {viewTarget.particulars.map((particular, index) => (
                    <li key={particular.id || `particular-${index}`}>
                      <span>{particular.description || '-'}</span>
                      <strong>{formatCurrency(particular.amount)}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="dashboard-empty">No particulars recorded.</span>
              )}
            </div>

            <div className="fd-field fd-field-wide">
              <label>Attachments</label>
              {Array.isArray(viewTarget.attachments) && viewTarget.attachments.length > 0 ? (
                <ul className="finance-attachment-list">
                  {viewTarget.attachments.map((attachment) => (
                    <li key={attachment.id || attachment.url}>
                      <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                        {attachment.name || 'Attachment'}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="dashboard-empty">No attachments uploaded for this request.</span>
              )}
            </div>
          </div>
        ) : null}
      </FdModal>
    </div>
  );
}
